import type { OrganizationType, RichTextDoc } from "@gately/validators";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import { Logo } from "@gately/ui/logo";
import { RichTextView } from "@gately/ui/rich-text-view";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@gately/ui/sheet";
import { StatusBadge } from "@gately/ui/status-badge";
import { toast } from "@gately/ui/toast";
import {
  formatRecurringSchedule,
  formatVisitInstant,
  formatVisitRange,
  getTerms,
  isOnRecurringSchedule,
  viewerTimeZone,
} from "@gately/validators";

import { PassNotice } from "~/components/pass/PassNotice";
import { SelfFillForm } from "~/components/pass/SelfFillForm";
import { orpc } from "~/lib/orpc";
import { derivePassPageState } from "~/lib/pass-page-state";
import { passUrl } from "~/lib/pass-share";

export const Route = createFileRoute("/pass/$token")({
  component: PassPage,
});

// Resolved once per module, not per render. A guest could be opening this
// link from anywhere — this is the surface most likely to actually need the
// "chez vous" secondary.
const VIEWER = viewerTimeZone();

const STATUS_LABEL: Record<string, string> = {
  active: "Valide",
  upcoming: "À venir",
  expired: "Expirée",
  revoked: "Révoquée",
  used: "Utilisée",
  // pass.get maps "off_schedule" to "active" on the wire (see comment at the
  // `valid` computation below) — this page computes it itself instead. Kept
  // here too so the map stays complete if a status ever arrives unmapped.
  off_schedule: "Hors créneau",
};

// Recurring's overall span is calendar-day bounds only (00:00/23:59
// placeholders — see the web app's buildValidity), never a real clock
// reading, so there is no instant here for `formatVisitInstant` to label a
// zone on.
function dateOnly(d: string | Date, timeZone: string): string {
  return new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    timeZone,
  });
}

function formatValidity(p: {
  type: string;
  validFrom: string | Date | null;
  validTo: string | Date | null;
  recurrence: string | null;
  dailyStartMinutes: number | null;
  dailyEndMinutes: number | null;
  /** The organization's IANA zone, sent by `pass.get`. A guest opening this
   *  link from another country must read the hours the GATE enforces, not the
   *  ones their own phone would show. */
  timeZone: string;
}): { primary: string; secondary: string | null } {
  const timeZone = p.timeZone;
  // Unique/Séjour show their time-of-day window so the hours are verifiable.
  // Recurring keeps a date-only span here (its daily window is appended
  // separately below via formatRecurringSchedule).
  const withTime = p.type === "one_time" || p.type === "date_range";
  let primary = "—";
  let secondary: string | null = null;
  if (p.validFrom && p.validTo) {
    if (withTime) {
      // One call for both ends — composing this from two independent
      // `formatVisitInstant` calls would print the zone label twice.
      const range = formatVisitRange(p.validFrom, p.validTo, timeZone, {
        viewerTimeZone: VIEWER,
        withDate: true,
      });
      primary = range.primary;
      secondary = range.secondary;
    } else {
      primary = `${dateOnly(p.validFrom, timeZone)} – ${dateOnly(p.validTo, timeZone)}`;
    }
  } else if (p.validFrom) {
    if (withTime) {
      const from = formatVisitInstant(p.validFrom, timeZone, {
        viewerTimeZone: VIEWER,
        withDate: true,
      });
      primary = `À partir du ${from.primary}`;
      secondary = from.secondary;
    } else {
      primary = `À partir du ${dateOnly(p.validFrom, timeZone)}`;
    }
  }
  if (p.type !== "recurring") return { primary, secondary };
  // e.g. "Lun, Mer, Ven · 08:00–18:00 · heure de Casablanca" — so a guest
  // reading "Hors créneau" can see which days/hours, on which clock, are
  // actually theirs.
  const schedule = formatRecurringSchedule(
    {
      recurrence: p.recurrence,
      dailyStartMinutes: p.dailyStartMinutes,
      dailyEndMinutes: p.dailyEndMinutes,
    },
    timeZone,
  );
  return {
    primary: schedule ? `${primary} · ${schedule}` : primary,
    secondary,
  };
}

function PassPage() {
  const { token } = Route.useParams();
  const {
    data: pass,
    isPending,
    isError,
  } = useQuery(orpc.pass.get.queryOptions({ input: { token } }));
  if (isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-neutral-950 text-sm text-neutral-400">
        Chargement…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-neutral-950 px-6 text-center text-sm text-neutral-400">
        Invitation introuvable ou expirée.
      </div>
    );
  }

  // One URL for the whole journey (spec §5): the form, a notice, or the pass.
  const state = derivePassPageState(pass, new Date());
  if (state !== "pass") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-neutral-950 p-5">
        <div className="w-full max-w-sm">
          {state === "form" ? (
            <SelfFillForm
              token={token}
              hostName={pass.hostName}
              validity={formatValidity(pass)}
            />
          ) : (
            <PassNotice kind={state} hostName={pass.hostName} />
          )}
        </div>
      </div>
    );
  }

  // pass.get deliberately maps "off_schedule" to "active" on the wire (the
  // fielded mobile app's statusLabel falls back to "Révoqué" for an unknown
  // status, which would tell a guest their valid pass was revoked). This page
  // derives the off-schedule state itself instead, from the additive
  // `recurrence` field, via the same shared schedule check the API uses.
  // Guarding on status === "active" keeps revoked/expired/used precedence
  // intact — none of those get relabeled "Hors créneau".
  const offSchedule =
    pass.type === "recurring" &&
    pass.status === "active" &&
    !isOnRecurringSchedule(
      {
        recurrence: pass.recurrence,
        dailyStartMinutes: pass.dailyStartMinutes,
        dailyEndMinutes: pass.dailyEndMinutes,
      },
      new Date(),
      pass.timeZone,
    );

  // An off-schedule pass keeps status === "active" under the wire mapping
  // above, so it stays `valid` here — the QR must stay visible: the pass
  // isn't dead, it's just not valid right now, and the guest needs it ready
  // for when it is.
  const valid = pass.status === "active" || pass.status === "upcoming";
  const url = passUrl(token);
  // "la résidence" / "l'immeuble" — an office visitor is not entering a
  // résidence. Undefined → residence wording (getTerms's default).
  const reglementNotice = getTerms(
    pass.orgType as OrganizationType | undefined,
  ).reglementAcceptanceNotice;

  const validity = formatValidity(pass);

  const card = (
    <div className="rounded-[24px] bg-neutral-900 p-6 shadow-lg">
      <div className="flex items-center justify-between">
        <Logo variant="light" withWordmark />
        <StatusBadge
          tone={offSchedule ? "warning" : valid ? "success" : "neutral"}
        >
          {offSchedule
            ? "Hors créneau"
            : (STATUS_LABEL[pass.status] ?? pass.status)}
        </StatusBadge>
      </div>

      {/* QR first: this page is pulled out at the gate, and nothing should
          push the code below the fold on a phone. */}
      <div className="mt-5 flex justify-center rounded-2xl bg-white p-4">
        <QRCodeSVG value={url} size={208} />
      </div>

      <h1 className="mt-5 text-center text-2xl font-semibold text-white">
        {pass.guestName}
      </h1>
      <p className="mt-1 text-center text-sm text-neutral-400">
        {pass.unitLabel} · {pass.residenceName}
      </p>
      {pass.address ? (
        <p className="mt-1 text-center text-xs text-neutral-500">
          {pass.address}
        </p>
      ) : null}
      {pass.locationLabel != null ? (
        <p className="mt-1 text-center text-xs text-neutral-500">
          Où nous trouver : {pass.locationLabel}
          {pass.locationDirections ? ` — ${pass.locationDirections}` : ""}
        </p>
      ) : null}
      {/* Also reaches guests who got this link over WhatsApp and so never
          saw the invitation email's "Comment venir". */}
      {pass.directionsUrl ? (
        <p className="mt-2 text-center">
          <a
            href={pass.directionsUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs font-medium text-neutral-300 underline underline-offset-4 hover:text-white"
          >
            Comment venir →
          </a>
        </p>
      ) : null}

      <div className="mt-5 flex justify-between gap-4 border-t border-neutral-800 pt-4">
        <div>
          <div className="font-mono text-[11px] tracking-wider text-neutral-500 uppercase">
            Validité
          </div>
          <div className="mt-1 text-sm font-medium text-white">
            {validity.primary}
            {validity.secondary && (
              <span className="mt-0.5 block text-xs font-normal text-neutral-400">
                {validity.secondary}
              </span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-mono text-[11px] tracking-wider text-neutral-500 uppercase">
            Invitation
          </div>
          <div className="mt-1 font-mono text-sm font-medium text-white">
            {pass.code}
          </div>
        </div>
      </div>

      {/* Below lg the règlement opens in a bottom sheet, so the page itself
          never becomes a long read with the QR scrolled away. At lg+ it sits
          in the reading column beside the card and this row is hidden. Email
          recipients get no PDF, so this page is the only place to read it. */}
      {pass.reglementDoc ? (
        <Sheet>
          <SheetTrigger asChild>
            <button
              type="button"
              className="mt-5 flex w-full items-center gap-3 rounded-2xl border border-neutral-800 bg-neutral-950/40 p-4 text-left transition-colors hover:border-neutral-700 lg:hidden"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-white">
                  Règlement intérieur
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-neutral-400">
                  {reglementNotice}
                </span>
              </span>
              <ChevronRight
                aria-hidden
                className="size-5 shrink-0 text-neutral-400"
              />
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="max-h-[90dvh] border-neutral-200 bg-white text-neutral-900"
          >
            <div className="border-b border-neutral-200 px-6 pt-5 pr-14 pb-4">
              <SheetTitle className="font-mono text-[11px] font-normal tracking-wider text-neutral-500 uppercase">
                Règlement intérieur
              </SheetTitle>
            </div>
            <div className="overflow-y-auto overscroll-contain px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              <ReglementBody doc={pass.reglementDoc} notice={reglementNotice} />
            </div>
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );

  const actions = (
    <>
      <button
        type="button"
        onClick={async () => {
          await navigator.clipboard.writeText(url);
          toast.success("Lien copié.");
        }}
        className="mt-4 flex h-11 w-full items-center justify-center rounded-lg border border-neutral-700 text-sm font-medium text-white hover:border-neutral-500"
      >
        Copier le lien
      </button>
      <p className="mt-4 px-1 text-[12px] leading-relaxed text-neutral-500">
        Vos coordonnées sont conservées par {pass.residenceName} pour faciliter
        vos prochaines visites pendant {pass.retentionMonths} mois ; vous pouvez
        demander leur suppression auprès de {pass.residenceName}.
      </p>
    </>
  );

  if (!pass.reglementDoc) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-neutral-950 p-5">
        <div className="w-full max-w-[420px]">
          {card}
          {actions}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-neutral-950 p-5 lg:px-8 lg:py-10">
      <div className="mx-auto grid w-full max-w-[420px] items-start gap-8 lg:max-w-[1100px] lg:grid-cols-[380px_minmax(0,1fr)]">
        {/* The card stays pinned while the règlement scrolls, so the QR is
            never more than a glance away. */}
        <div className="lg:sticky lg:top-10">
          {card}
          {actions}
        </div>

        <section
          aria-labelledby="reglement-title"
          className="hidden rounded-[24px] bg-white px-10 py-9 shadow-lg lg:block"
        >
          <h2
            id="reglement-title"
            className="font-mono text-[11px] tracking-wider text-neutral-500 uppercase"
          >
            Règlement intérieur
          </h2>
          <div className="mt-3 max-w-[68ch] border-t border-neutral-200 pt-5">
            <ReglementBody doc={pass.reglementDoc} notice={reglementNotice} />
          </div>
        </section>
      </div>
    </div>
  );
}

function ReglementBody({ doc, notice }: { doc: RichTextDoc; notice: string }) {
  return (
    <>
      <RichTextView
        doc={doc}
        className="space-y-3 text-[15px] leading-relaxed text-neutral-800"
      />
      <p className="mt-6 border-t border-neutral-200 pt-4 text-xs leading-relaxed text-neutral-500 italic">
        {notice}
      </p>
    </>
  );
}
